import { makeAutoObservable } from 'mobx'
import SystemSettingService from './../../../shared/services/SystemSetting.service'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UserService from './../../../shared/services/User.service'
import { UserEopolicyFactory } from './../../../shared/factories'
import { b64toBlob } from './../../../shared/utilities/b64toBlob.function'
import moment from 'moment'

const GROUP_POLICY = {
  loaded: false,
  url: null,
}

const serializeDate = (dateVal) => {
  if (dateVal && typeof dateVal === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(dateVal)) {
      dateVal = moment(dateVal, 'YYYY-MM-DDThh:mm:ss.SSSZ').format('YYYY-MM-DD')
    }
  }

  if (dateVal && typeof dateVal === 'object') {
    dateVal = moment(dateVal).utc().format('YYYY-MM-DD')
  }

  return dateVal
}

const getIsDatesValid = (Policy) => {
  let effective_date = Policy?.get('effective_date'),
    expiration_date = Policy?.get('expiration_date')

  effective_date =
    effective_date && typeof effective_date === 'string'
      ? moment(`${effective_date}`.split('T').shift()).toDate()
      : effective_date
  expiration_date =
    expiration_date && typeof expiration_date === 'string'
      ? moment(`${expiration_date}`.split('T').shift()).toDate()
      : expiration_date

  if (effective_date && expiration_date) {
    if (
      typeof effective_date === 'object' &&
      typeof expiration_date === 'object'
    ) {
      if (effective_date.getTime() >= expiration_date.getTime()) return false
    }
  }

  return true
}

function getIsValid(Policy) {
  // Only use the provided eo, do NOT close over eopolicy state!
  const isValid = !!(
    Policy?.id() &&
    Policy?.get('carrier_name') &&
    Policy?.get('per_coverage_amt') &&
    Policy?.get('agg_coverage_amt') &&
    Policy?.get('eopolicy_num') &&
    Policy?.get('eopolicy_pdf') &&
    Policy?.get('effective_date') &&
    Policy?.get('expiration_date')
  )

  if (isValid) return getIsDatesValid(Policy)

  return false
}

const fetchGroupEopolicy = async () => {
  if (GROUP_POLICY.loaded !== true) {
    try {
      const Policy = (
        (
          await SystemSettingService.search({
            search: { setting_key: 'vendors---eo-group-policy' },
          })
        )?.models || []
      ).shift()
      if (Policy && Policy.setting_value)
        GROUP_POLICY.url = JSON.parse(Policy.setting_value)?.url
      GROUP_POLICY.loaded = true
    } catch (ex) {
      GROUP_POLICY.loaded = true
    }
  }
}

const fetchPolicy = async (userId) => {
  let eopolicy = null

  try {
    eopolicy = await UserEopolicyFactory.findByUserId(userId)
    eopolicy = eopolicy
      ? eopolicy
      : await UserEopolicyFactory.create({ user_id: userId })
  } catch (ex) {}

  if (eopolicy?.get('effective_date'))
    eopolicy.set(
      'effective_date',
      moment(eopolicy.get('effective_date').split('T').shift()).toDate()
    )
  if (eopolicy?.get('expiration_date'))
    eopolicy.set(
      'expiration_date',
      moment(eopolicy.get('expiration_date').split('T').shift()).toDate()
    )

  if (GROUP_POLICY.loaded !== true) fetchGroupEopolicy()

  return eopolicy
}

class EopolicyStore {
  isLoading = false
  isSaving = false
  isDeleting = false
  isUploading = false
  User = null
  Policy = null
  showValidity = {}
  showModal = false
  dbTimer = null

  constructor() {
    makeAutoObservable(this)
  }

  getGroupUrl = () => GROUP_POLICY.loaded && GROUP_POLICY.url

  initUser = async (User) => {
    if (this.User?.id() === User?.id()) return false

    if (!User || !User?.id()) return false

    this.User = User

    this.isLoading = true

    // Fetch Agent policy.
    this.Policy = await fetchPolicy(this.User.id())

    // Set policy validity.
    ;[
      'effective_date',
      'expiration_date',
      'carrier_name',
      'per_coverage_amt',
      'agg_coverage_amt',
      'eopolicy_num',
    ].forEach((key) => {
      if (this.Policy.get(key)) this.showValidity[key] = false
    })

    this.isLoading = false
  }

  onSelectFile = (file) => {
    if (!this.Policy || !this.Policy?.id()) return

    const reader = new FileReader()

    reader.addEventListener('load', async () => {
      this.isUploading = true
      this.Policy.set('eopolicy_pdf_src', {
        filename: file?.name,
        data: reader.result,
      })

      // Extract content-type from complete base64 payload.
      let match = this.Policy.get('eopolicy_pdf_src')
        .data.split(',')[0]
        .match(/\w+\/\w+/)
      match = match && match.shift()

      // Extract raw base64 data w/out content-type.
      let base64 = this.Policy.get('eopolicy_pdf_src').data.split(',')[1]

      // Build payload to send file to the server.
      const formData = new FormData()
      formData.append('assumed_user_id', UserProfileService.getUserId(true))
      formData.append('eopolicy_pdf', b64toBlob(base64, match))
      formData.append('eopolicy_pdf_filename', file.name)
      formData.append('eopolicy_pdf_id', this.Policy.id())

      try {
        await UserService.uploadEopolicy(this.User.id(), formData)

        // UserEopolicyService.get(Policy?.id()).then((res) => {
        //   this.Policy.set('eopolicy_pdf', res && res?.eopolicy_pdf)
        // })
        this.Policy = await fetchPolicy(this.User.id())
      } catch (ex) {
        console.error('Failed to upload: ', ex)
      }

      this.isUploading = false
    })

    reader.readAsDataURL(file)
  }

  savePolicy = async (opts) => {
    const { debounce } = (opts = opts && typeof opts === 'object' ? opts : {})

    const completeSave = async () => {
      this.isSaving = true

      if (this.Policy.get('effective_date'))
        this.Policy.set(
          'effective_date',
          serializeDate(this.Policy.get('effective_date'))
        )

      if (this.Policy.get('expiration_date'))
        this.Policy.set(
          'expiration_date',
          serializeDate(this.Policy.get('expiration_date'))
        )

      try {
        await this.Policy.save()
        this.isSaving = false
        return true
      } catch (ex) {
        console.error(`${ex}`)
        this.isSaving = false
        return false
      }
    }

    if (debounce > 0) {
      if (this.dbTimer !== null) {
        window.clearTimeout(this.dbTimer)
        this.dbTimer = null
      }

      return await new Promise((resolve, reject) => {
        this.dbTimer = window.setTimeout(() => {
          completeSave()
            .then(
              (res) => resolve(res),
              (err) => reject(err)
            )
            .finally(() => {
              if (this.dbTimer !== null) {
                window.clearTimeout(this.dbTimer)
                this.dbTimer = null
              }
            })
        }, debounce)
      })
      return
    }

    return await completeSave()
  }

  deletePolicyPdf = async () => {
    this.isDeleting = true
    this.Policy.set('eopolicy_pdf', '')
    this.savePolicy()
    this.showModal = false
    this.isDeleting = false
    return true
  }

  getIsDatesValid = () => {
    return getIsDatesValid(this.Policy)
  }

  getIsValid = () => {
    return getIsValid(this.Policy)
  }
}

export default new EopolicyStore()
